/**
 * BasisEncoderWrapper - Browser-based KTX2 encoding using Basis Universal
 * Based on implementation from glb.babylonpress.org
 */

import { Logger } from './Logger';

const logger = Logger.getLogger('BasisEncoder');

export interface KTX2EncodeOptions {
  format: 'uastc' | 'etc1s';
  quality?: number;  // 1-255 for ETC1S, 0-3 for UASTC
  compressionLevel?: number; // 0-6 for ETC1S
  generateMipmaps?: boolean;
  srgb?: boolean;
}

/**
 * Minimal interface for the Basis WASM module (dynamically loaded)
 */
interface BasisModule {
  BasisEncoder: new () => BasisEncoder;
  initializeBasis(): void;
}

interface BasisEncoder {
  setCreateKTX2File(value: boolean): void;
  setKTX2UASTCSupercompression(value: boolean): void;
  setKTX2SRGBTransferFunc(value: boolean): void;
  setUASTC(value: boolean): void;
  setPackUASTCFlags(quality: number): void;
  setQualityLevel(quality: number): void;
  setCompressionLevel(level: number): void;
  setMipGen(value: boolean): void;
  setSliceSourceImage(sliceIndex: number, data: Uint8Array, width: number, height: number, isPremultiplied: boolean): boolean;
  encode(outputBuffer: Uint8Array): number;
  delete(): void;
}

export class BasisEncoderWrapper {
  private static moduleLoaded: boolean = false;
  private static basisModule: BasisModule | null = null;
  private static isLoading: boolean = false;
  private static loadPromise: Promise<void> | null = null;

  /**
   * Load Basis Universal WASM module
   * Uses CDN-hosted version for convenience
   */
  static async loadModule(): Promise<void> {
    if (this.moduleLoaded) return;
    if (this.isLoading && this.loadPromise) return this.loadPromise;

    this.isLoading = true;
    this.loadPromise = this.initializeModule();
    return this.loadPromise;
  }

  /**
   * Initialize the Basis module (separated for cleaner async handling)
   */
  private static async initializeModule(): Promise<void> {
    try {
      logger.info('Loading Basis Universal WASM encoder...');

      // Load locally-hosted Basis encoder (downloaded via setup script)
      // Run: npm run setup-ktx2 to download the WASM files
      const scriptUrl = '/assets/basis_encoder.js';

      // Load script
      await this.loadScript(scriptUrl);

      // Initialize module (exposed as global BASIS)
      if (typeof window.BASIS === 'function') {
        this.basisModule = await window.BASIS({
          locateFile: (filename: string) => {
            // Point to locally-hosted WASM file
            if (filename.endsWith('.wasm')) {
              return '/assets/basis_encoder.wasm';
            }
            return filename;
          }
        }) as BasisModule;

        // Initialize basis
        this.basisModule.initializeBasis();
        this.moduleLoaded = true;

        logger.info('Basis Universal encoder loaded and initialized');
      } else {
        throw new Error('BASIS module not found after script load');
      }
    } catch (error) {
      logger.error('Failed to load Basis encoder:', error);
      this.isLoading = false;
      throw error;
    }
  }

  /**
   * Load external script
   */
  private static loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Encode raw RGBA data to KTX2 format
   */
  static async encodeRGBAToKTX2(
    rgbaData: Uint8Array,
    width: number,
    height: number,
    options: KTX2EncodeOptions = { format: 'uastc', generateMipmaps: true, srgb: true }
  ): Promise<Uint8Array> {
    // Ensure module is loaded
    await this.loadModule();

    if (!this.basisModule) {
      throw new Error('Basis module not loaded');
    }

    const Module = this.basisModule;

    // Create encoder instance
    const encoder = new Module.BasisEncoder();

    try {
      // Configure encoder
      encoder.setCreateKTX2File(true);
      encoder.setKTX2UASTCSupercompression(true); // Zstandard compression
      encoder.setKTX2SRGBTransferFunc(options.srgb !== false);

      // Set format
      if (options.format === 'uastc') {
        encoder.setUASTC(true);
        const quality = options.quality !== undefined ? options.quality : 2; // 0-3, default 2
        encoder.setPackUASTCFlags(quality);
      } else {
        // ETC1S
        encoder.setUASTC(false);
        const quality = options.quality !== undefined ? options.quality : 128;
        const compressionLevel = options.compressionLevel !== undefined ? options.compressionLevel : 2;
        encoder.setQualityLevel(quality); // 1-255
        encoder.setCompressionLevel(compressionLevel); // 0-6
      }

      // Mipmap generation
      encoder.setMipGen(options.generateMipmaps !== false);

      // Set source image as raw RGBA pixel data (not PNG file!)
      const sliceIndex = 0;

      logger.debug(`Passing RGBA data to encoder: ${rgbaData.length} bytes (${width}×${height})`);

      // Encoder expects raw RGBA pixels: setSliceSourceImage(sliceIndex, dataArray, width, height, isPremultiplied)
      const result = encoder.setSliceSourceImage(
        sliceIndex,
        rgbaData,  // Raw RGBA pixel data from canvas
        width,
        height,
        false  // isPremultiplied (canvas ImageData is not premultiplied)
      );

      logger.debug(`setSliceSourceImage result: ${result}`);

      // Allocate output buffer (generous size)
      const maxOutputSize = width * height * 4;
      const outputBuffer = new Uint8Array(maxOutputSize);

      // Encode
      logger.debug(`Encoding ${width}×${height} to ${options.format.toUpperCase()}...`);
      const startTime = performance.now();

      const outputSize = encoder.encode(outputBuffer);

      const elapsedTime = ((performance.now() - startTime) / 1000).toFixed(2);
      logger.info(`Encoded in ${elapsedTime}s, output: ${(outputSize / 1024).toFixed(1)} KB`);

      if (outputSize === 0) {
        throw new Error('Encoding failed - output size is 0');
      }

      // Extract actual data
      const ktx2Data = new Uint8Array(outputBuffer.buffer, 0, outputSize);

      return ktx2Data;

    } finally {
      // Clean up encoder
      encoder.delete();
    }
  }

  /**
   * Encode canvas to KTX2
   * Extracts raw RGBA pixel data from canvas and encodes to KTX2
   */
  static async encodeCanvasToKTX2(
    canvas: HTMLCanvasElement,
    options: KTX2EncodeOptions
  ): Promise<Uint8Array> {
    logger.debug(`Extracting RGBA data from canvas ${canvas.width}×${canvas.height}...`);

    // Get raw RGBA pixel data from canvas (this is what the encoder actually wants!)
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Failed to get canvas 2D context');
    }

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const rgbaData = new Uint8Array(imageData.data.buffer);

    logger.debug(`Extracted ${rgbaData.length} bytes of RGBA data`);

    // Encode raw RGBA to KTX2
    return this.encodeRGBAToKTX2(rgbaData, canvas.width, canvas.height, options);
  }

  /**
   * Check if WASM threading is supported
   */
  static isThreadingSupported(): boolean {
    return typeof SharedArrayBuffer !== 'undefined';
  }
}
